import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdSave,
  MdInfo,
  MdRefresh,
} from 'react-icons/md';
import ZodiacIcon from '../components/ZodiacIcons';

const DEFAULT_ZODIAC_SIGNS = [
  { zodiac_id: 1, name: 'మేషం', english_name: 'Aries', icon_key: 'aries', color: '#4CAF50', sort_order: 1 },
  { zodiac_id: 2, name: 'వృషభం', english_name: 'Taurus', icon_key: 'taurus', color: '#F44336', sort_order: 2 },
  { zodiac_id: 3, name: 'మిధునం', english_name: 'Gemini', icon_key: 'gemini', color: '#E91E63', sort_order: 3 },
  { zodiac_id: 4, name: 'కర్కాటకం', english_name: 'Cancer', icon_key: 'cancer', color: '#3F51B5', sort_order: 4 },
  { zodiac_id: 5, name: 'సింహం', english_name: 'Leo', icon_key: 'leo', color: '#EC407A', sort_order: 5 },
  { zodiac_id: 6, name: 'కన్య', english_name: 'Virgo', icon_key: 'virgo', color: '#283593', sort_order: 6 },
  { zodiac_id: 7, name: 'తుల', english_name: 'Libra', icon_key: 'libra', color: '#009688', sort_order: 7 },
  { zodiac_id: 8, name: 'వృశ్చికం', english_name: 'Scorpio', icon_key: 'scorpio', color: '#E65100', sort_order: 8 },
  { zodiac_id: 9, name: 'ధనుస్సు', english_name: 'Sagittarius', icon_key: 'sagittarius', color: '#43A047', sort_order: 9 },
  { zodiac_id: 10, name: 'మకరం', english_name: 'Capricorn', icon_key: 'capricorn', color: '#EF6C00', sort_order: 10 },
  { zodiac_id: 11, name: 'కుంభం', english_name: 'Aquarius', icon_key: 'aquarius', color: '#C62828', sort_order: 11 },
  { zodiac_id: 12, name: 'మీనం', english_name: 'Pisces', icon_key: 'pisces', color: '#2E7D32', sort_order: 12 },
];

export default function DailyHoroscope() {
  const [horoscopes, setHoroscopes] = useState(() =>
    DEFAULT_ZODIAC_SIGNS.map(z => ({ ...z, prediction: '' }))
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const fetchCurrentHoroscopes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/horoscopes');
      if (res.data?.success && res.data?.data) {
        const fetched = res.data.data || [];
        setHoroscopes(
          DEFAULT_ZODIAC_SIGNS.map(def => {
            const match = fetched.find(f => f.zodiac_id === def.zodiac_id);
            return {
              ...def,
              prediction: match ? match.prediction || '' : '',
              horoscope_id: match ? match.id : null,
            };
          })
        );
        if (fetched.length > 0) {
          setLastSavedAt(fetched[0].updated_at || null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch horoscope:', err);
      toast.error('Failed to load horoscope');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentHoroscopes();
  }, [fetchCurrentHoroscopes]);

  const handlePredictionChange = (zodiacId, text) => {
    setHoroscopes(prev =>
      prev.map(item =>
        item.zodiac_id === zodiacId ? { ...item, prediction: text } : item
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = horoscopes.map(h => ({
        zodiac_id: h.zodiac_id,
        prediction: h.prediction || '',
      }));

      const res = await api.put('/admin/horoscopes', payload);
      if (res.data?.success) {
        toast.success('Today\'s horoscope updated successfully!');
        fetchCurrentHoroscopes();
      } else {
        toast.error(res.data?.message || 'Failed to save horoscope');
      }
    } catch (err) {
      console.error('Error saving horoscope:', err);
      toast.error(err.response?.data?.message || 'Failed to save horoscope');
    } finally {
      setSaving(false);
    }
  };

  const filledCount = horoscopes.filter(h => (h.prediction || '').trim().length > 0).length;

  return (
    <div className="daily-horoscope-page">
      {/* Simple Header with Title and Buttons */}
      <div className="horoscope-top-header">
        <h1 className="horoscope-main-title">Today's Horoscope</h1>

        {/* Save and Refresh Buttons */}
        <div className="horoscope-controls-bar">
          <button
            type="button"
            className="refresh-horoscope-btn"
            onClick={fetchCurrentHoroscopes}
            disabled={loading || saving}
            title="Refresh latest horoscope"
          >
            <MdRefresh size={20} />
          </button>

          <button
            type="button"
            className="save-horoscope-btn"
            onClick={handleSave}
            disabled={saving || loading}
          >
            <MdSave size={20} />
            <span>{saving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>

      {/* Main 12 Zodiac 2-Column Grid */}
      <div className="horoscope-grid-container">
        {loading ? (
          <div className="horoscope-loading-state">
            <div className="loading-spinner" />
            <p>Loading today's horoscope predictions...</p>
          </div>
        ) : (
          <div className="horoscope-two-columns">
            {/* Left Column: Signs 1 to 6 (మేషం through కన్య) */}
            <div className="horoscope-col">
              {horoscopes.slice(0, 6).map((zodiac) => (
                <div
                  key={zodiac.zodiac_id}
                  className="horoscope-card"
                  style={{
                    '--badge-color': zodiac.color,
                  }}
                >
                  {/* Left: Icon Badge & Telugu Name */}
                  <div className="horoscope-card-left">
                    <ZodiacIcon
                      name={zodiac.name}
                      iconKey={zodiac.icon_key}
                      size={52}
                      className="horoscope-badge-icon"
                    />
                    <span className="horoscope-zodiac-name">{zodiac.name}</span>
                  </div>

                  {/* Right: Textarea for Prediction */}
                  <div className="horoscope-card-right">
                    <textarea
                      className="horoscope-textarea"
                      rows={3}
                      placeholder="ఈ రోజు మీ రాశి ఫలాలు..."
                      value={zodiac.prediction || ''}
                      onChange={(e) => handlePredictionChange(zodiac.zodiac_id, e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column: Signs 7 to 12 (తుల through మీనం) */}
            <div className="horoscope-col">
              {horoscopes.slice(6, 12).map((zodiac) => (
                <div
                  key={zodiac.zodiac_id}
                  className="horoscope-card"
                  style={{
                    '--badge-color': zodiac.color,
                  }}
                >
                  {/* Left: Icon Badge & Telugu Name */}
                  <div className="horoscope-card-left">
                    <ZodiacIcon
                      name={zodiac.name}
                      iconKey={zodiac.icon_key}
                      size={52}
                      className="horoscope-badge-icon"
                    />
                    <span className="horoscope-zodiac-name">{zodiac.name}</span>
                  </div>

                  {/* Right: Textarea for Prediction */}
                  <div className="horoscope-card-right">
                    <textarea
                      className="horoscope-textarea"
                      rows={3}
                      placeholder="ఈ రోజు మీ రాశి ఫలాలు..."
                      value={zodiac.prediction || ''}
                      onChange={(e) => handlePredictionChange(zodiac.zodiac_id, e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Status Note */}
      {!loading && (
        <div className="horoscope-footer-notice">
          <span className="meta-badge">{filledCount} / 12 Signs Filled</span>
          {lastSavedAt && (
            <span className="meta-saved-time">
              Last updated: {new Date(lastSavedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
